import { Box, Grid, Typography } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { ThemePalette } from 'mui.theme';
import { useAppSelector } from 'store/hook';
import { convertSeconds } from 'views/Production/AllFleet/utils';
import TableList from 'views/Production/components/Table/TableList';
import { FlexBox } from 'components/Containers/FlexBox';
import { truncateTo2dp } from 'utils/helpers/general';
import type { AveragrCycleTimeType } from 'types/production.types';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  isLoading?: boolean;
}

export const LoaderEventsDuration = ({
  enableDarkTheme,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const {
    data: {
      allFleet: { loaderEvents },
    },
  } = useAppSelector((state) => state.productionReducer);

  const colors = ['#A776FF', '#FF0068', '#FF9C2C', '#165BAA'];
  const formatKey = (str: string) =>
    str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const eventsDurationChartData: AveragrCycleTimeType[] = loaderEvents.map(
    (item, index) => {
      return {
        value: item.current_duration,
        diff: item.duration_percentage_diff,
        color: colors[index],
        name: formatKey(item.event_type),
        time: `${truncateTo2dp(convertSeconds(item.current_duration).value)} ${
          convertSeconds(item.current_duration).unit
        }`,
        direction: item.duration_percentage_diff > 0 ? 'up' : 'down',
      };
    }
  );

  const hasChartData = eventsDurationChartData.some(
    (obj) => obj.value && obj.value !== 0
  );

  return (
    <Grid item lg={6}>
      <Box
        sx={{
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
          padding: 2,
          minHeight: '300px',
          maxHeight: '300px',
        }}
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {t('average_loader_events_duration')}
        </Typography>
        <Box
          sx={{
            paddingTop: '10px',
          }}
        >
          {isLoading ? (
            <MultiColorCircularLoader height="300px" />
          ) : (
            <Grid container>
              <Grid item lg={6} sm={12}>
                {' '}
                <Box
                  sx={{
                    minHeight: '200px',
                    maxHeight: '200px',
                  }}
                >
                  {hasChartData ? (
                    <FlexBox
                      sx={{
                        maxWidth: 'inherit',
                        height: '230px',
                        paddingTop: '10px',
                        marginRight: '10px',
                        paddingBottom: '10px',
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart width={400} height={400}>
                          <Pie
                            data={eventsDurationChartData}
                            dataKey="value"
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={90}
                          >
                            {eventsDurationChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            cursor={{ fill: '#1B2B4A' }}
                            wrapperStyle={{
                              outline: 'none',
                              fontSize: '11px',
                            }}
                            contentStyle={{
                              borderColor: 'rgba(255, 255, 255, 0.1)',
                              backgroundColor: enableDarkTheme
                                ? 'rgba(0,0,0, 0.6)'
                                : 'rgba(255, 255, 255, 0.6)',
                              borderRadius: '4px',
                            }}
                            itemStyle={{
                              color: enableDarkTheme
                                ? ThemePalette.typography.wheat
                                : ThemePalette.typography.black,
                            }}
                            formatter={(value: any, name, props) => {
                              const formattedResp = convertSeconds(value);
                              return [
                                `${truncateTo2dp(formattedResp.value)} ${
                                  formattedResp.unit
                                }`,
                                name,
                              ];
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </FlexBox>
                  ) : (
                    <Typography
                      sx={{
                        marginTop: '20px',
                        fontSize: '11px',
                      }}
                    >
                      {t('no_data_available_currently')}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item lg={6} sm={12}>
                {hasChartData ? (
                  <TableList
                    enableDarkTheme={enableDarkTheme}
                    data={eventsDurationChartData}
                  />
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default LoaderEventsDuration;
